import { InMemoryWebStorage, WebStorageStateStore } from "oidc-client-ts";

/**
 * Runtime configuration, supplied by Vite env vars at build time.
 *
 * Every value comes from a Terraform output. Missing config fails loudly here
 * rather than as a confusing OIDC redirect loop later.
 */

function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `${name} is not set. Copy .env.example to .env.local and fill it from ` +
        `\`terraform -chdir=infra/envs/dev output\`.`,
    );
  }
  return value;
}

export const config = {
  apiBaseUrl: required("VITE_API_BASE_URL", import.meta.env.VITE_API_BASE_URL),
  region: required("VITE_AWS_REGION", import.meta.env.VITE_AWS_REGION),
  userPoolId: required("VITE_USER_POOL_ID", import.meta.env.VITE_USER_POOL_ID),
  clientId: required("VITE_USER_POOL_CLIENT_ID", import.meta.env.VITE_USER_POOL_CLIENT_ID),
  hostedUiDomain: required("VITE_HOSTED_UI_DOMAIN", import.meta.env.VITE_HOSTED_UI_DOMAIN),
  environment: required("VITE_ENVIRONMENT", import.meta.env.VITE_ENVIRONMENT),
} as const;

/**
 * Anything that is not production, named.
 *
 * Dev holds real client ledgers, so the two environments look identical once
 * you are signed in. Saying which is which is the difference between deleting
 * a test client and deleting a real one.
 */
export function nonProductionLabel(): string | null {
  const name = config.environment.toLowerCase();
  return name === "prod" || name === "production" ? null : config.environment;
}

export const oidcConfig = {
  authority: `https://cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`,
  client_id: config.clientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  response_type: "code",
  scope: "openid email profile",
  // Tokens in memory only -- and it has to be said out loud, because
  // oidc-client-ts defaults userStore to window.sessionStorage. Leaving it
  // unset put the access token and a thirty-day refresh token in storage any
  // injected script or browser extension can read, while the comment here
  // claimed otherwise. Signing in again after a refresh is the price, and
  // staff sign in rarely.
  userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
  automaticSilentRenew: true,
} as const;

/** Cognito's hosted UI does not implement RP-initiated logout, so sign-out
 *  has to hit its own /logout endpoint to clear the session cookie. */
export function hostedLogoutUrl(): string {
  const url = new URL(`https://${config.hostedUiDomain}.auth.${config.region}.amazoncognito.com/logout`);
  url.searchParams.set("client_id", config.clientId);
  url.searchParams.set("logout_uri", window.location.origin);
  return url.toString();
}
